import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, TextField, Button, Typography, InputAdornment, IconButton, Alert, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff, MonitorHeart } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)' }}>
      {/* Left Panel */}
      <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', justifyContent: 'center', px: 8, color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
          <Box sx={{ bgcolor: '#3b82f6', p: 1.2, borderRadius: 2 }}>
            <MonitorHeart sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }}>MedelaHub HMS</Typography>
        </Box>
        <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 2 }}>
          Complete Hospital<br />Management Platform
        </Typography>
        <Typography sx={{ color: '#94a3b8', fontSize: 16, mb: 4, lineHeight: 1.7 }}>
          Manage patients, appointments, inventory, and billing — all in one place. Built for modern healthcare.
        </Typography>
        {[
          '🏥 Multi-hospital onboarding',
          '👥 Patient registry & records',
          '📦 Inventory with low-stock alerts',
          '💰 Billing & revenue tracking',
        ].map(f => (
          <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography sx={{ color: '#cbd5e1', fontSize: 15 }}>{f}</Typography>
          </Box>
        ))}
      </Box>

      {/* Right Panel - Login Form */}
      <Box sx={{ width: { xs: '100%', md: 480 }, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, bgcolor: '#f8fafc' }}>
        <Card elevation={0} sx={{ width: '100%', maxWidth: 400, borderRadius: 4, border: '1px solid #e2e8f0' }}>
          <CardContent sx={{ p: 4 }}>
            {/* Mobile Logo */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box sx={{ bgcolor: '#3b82f6', p: 1, borderRadius: 2 }}>
                <MonitorHeart sx={{ fontSize: 24, color: 'white' }} />
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: 18, color: '#0f172a' }}>MedelaHub HMS</Typography>
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>Welcome back</Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Sign in to your HMS account</Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>{error}</Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8 }}>Email Address</Typography>
              <TextField
                fullWidth type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email" required
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', '&:hover fieldset': { borderColor: '#3b82f6' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6' } } }}
              />

              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8 }}>Password</Typography>
              <TextField
                fullWidth type={showPass ? 'text' : 'password'} value={password}
                onChange={e => setPassword(e.target.value)} placeholder="Enter your password" required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPass(!showPass)} size="small">
                        {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', '&:hover fieldset': { borderColor: '#3b82f6' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6' } } }}
              />

              <Button
                type="submit" fullWidth variant="contained" disabled={loading} size="large"
                sx={{ py: 1.5, borderRadius: 2, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, fontWeight: 700, fontSize: 15, textTransform: 'none', boxShadow: '0 4px 14px rgba(30,64,175,0.4)' }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Sign In'}
              </Button>
            </form>

            <Box sx={{ mt: 3, p: 2, bgcolor: '#f1f5f9', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 1 }}>Demo Credentials</Typography>
              {[
                { role: 'Super Admin', email: 'superadmin@hms.com', pass: 'Admin@123' },
                { role: 'Admin', email: 'admin@cityhospital.com', pass: 'Admin@123' },
              ].map(c => (
                <Box
                  key={c.role}
                  onClick={() => { setEmail(c.email); setPassword(c.pass); }}
                  sx={{ cursor: 'pointer', p: 1, borderRadius: 1, '&:hover': { bgcolor: '#e2e8f0' }, mb: 0.5 }}
                >
                  <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600 }}>{c.role}: </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>{c.email}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Login;
